import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBranches } from '../../api/salon';
import {
  createService,
  deleteService,
  fetchServiceHistory,
  fetchServiceSuggestions,
  fetchServices,
  updateService,
} from '../../api/services';
import ServiceFormPanel from '../../components/services/ServiceFormPanel';
import ServiceHistoryModal from '../../components/services/ServiceHistoryModal';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { CATEGORIES } from '../../constants/serviceCategories';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useToast } from '../../context/ToastContext';
import { formatServiceBranchLabel } from '../../utils/serviceBranches';

const SalonServicesPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['service-suggestions', category],
    fetchSuggestions: (q) => fetchServiceSuggestions({ q, category: category || undefined }),
  });
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['services', category, branchFilter, page, pageSize, appliedSearch],
    queryFn: () =>
      fetchServices({
        category: category || undefined,
        branchId: branchFilter || undefined,
        search: appliedSearch || undefined,
        isActive: undefined,
        page,
        limit: pageSize,
      }),
  });

  const branches = branchData?.branches || [];
  const serviceBranchLabel = (service) =>
    formatServiceBranchLabel(service, branches, t('salon.services.allBranches'));
  const services = data?.services || [];
  const pagination = data?.pagination;

  const saveMut = useMutation({
    mutationFn: ({ id, payload }) =>
      id
        ? updateService(id, { ...payload.fields, isActive: payload.fields.isActive }, payload.imageFile)
        : createService(payload.fields, payload.imageFile),
    onSuccess: () => {
      showToast(editing ? t('toast.serviceUpdated') : t('toast.serviceCreated'), 'success');
      setPanelOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const toggleMut = useMutation({
    mutationFn: (service) =>
      updateService(service._id, { isActive: !service.isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['services'] }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      showToast(t('toast.serviceRemoved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const openHistory = async (service) => {
    const res = await fetchServiceHistory(service._id);
    setHistoryData(res.changeHistory);
    setEditing(service);
    setHistoryOpen(true);
  };

  return (
    <DashboardShell title={t('salon.services.title')} subtitle={t('salon.services.subtitle')}>
      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('salon.services.searchPlaceholder')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setCategory(''); setPage(1); }}
            className={`rounded-lg px-3 py-1.5 text-sm ${!category ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
          >
            {t('common.all')}
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { setCategory(c); setPage(1); }}
              className={`rounded-lg px-3 py-1.5 text-sm ${category === c ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
            >
              {c}
            </button>
          ))}
          <select
            value={branchFilter}
            onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-1.5 text-sm"
          >
            <option value="">{t('salon.services.allBranches')}</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>
      </ListSearchToolbar>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => { setEditing(null); setPanelOpen(true); }}
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white"
        >
          {t('salon.services.addServiceButton')}
        </button>
      </div>

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((service) => (
          <div key={service._id} className={`rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden ${!service.isActive ? 'opacity-60' : ''}`}>
            {service.imageUrl && (
              <img src={service.imageUrl} alt="" className="h-36 w-full object-cover" />
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium">{service.name}</h3>
                <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs text-zinc-600 dark:text-zinc-400">{service.category}</span>
              </div>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
                {t('common.duration', { minutes: service.duration })} · {t('common.currency', { amount: service.price?.toLocaleString() })}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {t('salon.services.branch')}: {serviceBranchLabel(service)}
              </p>
              <label className="mt-3 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
                <input type="checkbox" checked={service.isActive} onChange={() => toggleMut.mutate(service)} />
                {t('salon.services.activeLabel')}
              </label>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={() => { setEditing(service); setPanelOpen(true); }} className="text-xs text-glow-400 hover:underline">{t('common.edit')}</button>
                <button type="button" onClick={() => openHistory(service)} className="text-xs text-zinc-600 dark:text-zinc-400 hover:underline">{t('salon.services.history')}</button>
                <button type="button" onClick={() => deleteMut.mutate(service._id)} className="text-xs text-red-400 hover:underline">{t('common.delete')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <ServiceFormPanel
        open={panelOpen}
        onClose={() => { setPanelOpen(false); setEditing(null); }}
        service={editing}
        branches={branches}
        saving={saveMut.isPending}
        onSave={(fields) => {
          saveMut.mutate({
            id: editing?._id,
            payload: {
              fields: {
                name: fields.name,
                description: fields.description,
                category: fields.category,
                duration: fields.duration,
                price: fields.price,
                branchIds: fields.branchIds,
                requiresAdvance: fields.requiresAdvance,
                advanceAmount: fields.advanceAmount,
                paymentSlipPolicy: fields.paymentSlipPolicy,
                isActive: fields.isActive,
              },
              imageFile: fields.imageFile,
            },
          });
        }}
      />

      <ServiceHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData}
        serviceName={editing?.name}
      />
    </DashboardShell>
  );
};

export default SalonServicesPage;
